import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

interface Toast {
  id: number
  text: string
  bad: boolean
}

interface ToastApi {
  /** Something worked. */
  ok: (text: string) => void
  /** Something failed. A failure never renders with a success mark. */
  fail: (text: string) => void
}

const Ctx = createContext<ToastApi | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const next = useRef(0)

  const push = useCallback((text: string, bad: boolean) => {
    const id = ++next.current
    setToasts((t) => [...t, { id, text, bad }])
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3200)
  }, [])

  const api = useMemo<ToastApi>(
    () => ({ ok: (t) => push(t, false), fail: (t) => push(t, true) }),
    [push],
  )

  return (
    <Ctx.Provider value={api}>
      {children}
      <div className="toasts" aria-live="polite" aria-atomic="false">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.bad ? 'toast--bad' : 'toast--ok'}`} role="status">
            <span className="toast__mark" aria-hidden="true">
              {t.bad ? '!' : '✓'}
            </span>
            <span>
              <span className="sr-only">{t.bad ? 'Failed: ' : 'Done: '}</span>
              {t.text}
            </span>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  )
}

export function useToast(): ToastApi {
  const api = useContext(Ctx)
  if (!api) throw new Error('useToast must be used inside ToastProvider')
  return api
}
